import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react'

const COOKIE_NAME_OPENAI_API_KEY = 'user_openai_apiKey'
const COOKIE_MAX_AGE_DAYS = 365

const getCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

const setCookie = (name: string, value: string, maxAgeDays: number) => {
  const maxAge = maxAgeDays * 24 * 60 * 60
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Strict; Secure`
}

const deleteCookie = (name: string) => {
  document.cookie = `${name}=; path=/; max-age=0; SameSite=Strict; Secure`
}

type UserSettingsContextValue = {
  openAiApiKey: string | null
  setOpenAiApiKey: (key: string | null) => void
}

const UserSettingsContext = createContext<UserSettingsContextValue>({
  openAiApiKey: null,
  setOpenAiApiKey: () => {},
})

export const UserSettingsProvider = ({ children }: { children: ReactNode }) => {
  const [openAiApiKey, setOpenAiApiKeyState] = useState<string | null>(() => {
    try {
      return getCookie(COOKIE_NAME_OPENAI_API_KEY)
    } catch {
      return null
    }
  })

  const setOpenAiApiKey = useCallback((key: string | null) => {
    setOpenAiApiKeyState(key)
    try {
      if (key) {
        setCookie(COOKIE_NAME_OPENAI_API_KEY, key, COOKIE_MAX_AGE_DAYS)
      } else {
        deleteCookie(COOKIE_NAME_OPENAI_API_KEY)
      }
    } catch {
      // cookies unavailable
    }
  }, [])

  const value = useMemo(() => ({ openAiApiKey, setOpenAiApiKey }), [openAiApiKey, setOpenAiApiKey])

  return <UserSettingsContext.Provider value={value}>{children}</UserSettingsContext.Provider>
}

export const useUserSettings = () => useContext(UserSettingsContext)
